import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useUserImpersonation } from '@/contexts/UserImpersonationContext';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { Badge } from '@/components/ui/badge';
import { Users, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserImpersonationDropdownProps {
  className?: string;
}

export function UserImpersonationDropdown({ className }: UserImpersonationDropdownProps) {
  const { t } = useTranslation('platformAdmin');
  const {
    impersonatedUser,
    tenantUsers,
    loading,
    startImpersonation,
    fetchTenantUsers
  } = useUserImpersonation();
  
  const { selectedTenant } = useTenantSelection();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    if (!selectedTenant?.schema_name) return;

    const timeoutId = setTimeout(() => {
      fetchTenantUsers(selectedTenant.schema_name);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [selectedTenant?.schema_name, fetchTenantUsers]);

  // Filter users based on search query
  const filteredUsers = tenantUsers.filter(user => 
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartImpersonation = async (userAuthId: string) => {
    if (!selectedTenant) {
      console.error('❌ No tenant selected for impersonation');
      return;
    }
    
    console.log('🎭 Dropdown: Starting impersonation for user:', userAuthId);
    
    try {
      await startImpersonation(userAuthId, selectedTenant.id);
      console.log('✅ Dropdown: Impersonation successful, closing dropdown');
      setIsOpen(false);
      setSearchQuery('');
    } catch (error) {
      console.error('💥 Dropdown: Impersonation failed:', error);
      // Error is already handled in the context with toast
    }
  };

  if (!selectedTenant) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={impersonatedUser ? "secondary" : "outline"}
            size="sm"
            className="flex items-center gap-2"
            disabled={loading}
          >
            <Users className="h-4 w-4" />
            {impersonatedUser ? t('impersonation.switchUser') : t('impersonation.impersonateUser')}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('impersonation.selectUser')}
          </DropdownMenuLabel>

          <div className="px-2 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('impersonation.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
          </div>

          <DropdownMenuSeparator />

          {loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {t('impersonation.loadingUsers')}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {searchQuery ? t('impersonation.noMatches') : t('impersonation.noUsers')}
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {filteredUsers.map((user) => (
                <DropdownMenuItem
                  key={user.auth_user_id}
                  onClick={() => handleStartImpersonation(user.auth_user_id)}
                  disabled={loading || impersonatedUser?.auth_user_id === user.auth_user_id}
                  className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium text-sm">
                      {user.full_name || user.email}
                    </span>
                    <div className="flex items-center gap-1">
                      <Badge 
                        variant={user.is_active ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {user.role}
                      </Badge>
                      {impersonatedUser?.auth_user_id === user.auth_user_id && (
                        <Badge variant="outline" className="text-xs">
                          {t('impersonation.current')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {user.email}
                  </span>
                  {!user.is_active && (
                    <span className="text-xs text-destructive">
                      {t('impersonation.inactive')}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}